import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, X, Check } from "lucide-react";
import { Input } from "./ui/input";
import { api, errMsg } from "../lib/api";

const toOrder = (c) => ({ customer_id: c.id, customer_name: c.name, customer_phone: c.phone, customer_address: c.address,
  customer_tax_office: c.tax_office, customer_tax_no: c.tax_no });

export const CustomerPicker = ({ value, onChange }) => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/customers").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const q = (value.customer_name || "").toLocaleLowerCase("tr");
  const matches = useMemo(() => list.filter((c) => !q || [c.name, c.phone, c.tax_no].some((v) => (v || "").toLocaleLowerCase("tr").includes(q))).slice(0, 8), [list, q]);
  const set = (k, v) => onChange({ ...value, [k]: v, ...(k === "customer_name" ? { customer_id: "" } : {}) });

  const save = async () => {
    try {
      const { data } = await api.post("/customers", { name: value.customer_name, phone: value.customer_phone, address: value.customer_address,
        tax_office: value.customer_tax_office, tax_no: value.customer_tax_no });
      toast.success("Müşteri listeye kaydedildi");
      onChange({ ...value, customer_id: data.id });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="order-customer-name-input" placeholder="Müşteri ara veya yeni unvan yazın *" value={value.customer_name}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => {
            const v = e.target.value;
            const exact = list.find((c) => c.name.toLocaleLowerCase("tr") === v.trim().toLocaleLowerCase("tr"));
            if (exact) { onChange({ ...value, ...toOrder(exact) }); setOpen(false); } else { set("customer_name", v); setOpen(true); }
          }} className="pl-10 pr-9" />
        {value.customer_id && <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" data-testid="order-customer-selected" />}
        {open && matches.length > 0 && !value.customer_id && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg" data-testid="customer-suggestions">
            {matches.map((c) => (
              <button key={c.id} type="button" data-testid={`customer-option-${c.id}`} onMouseDown={() => { onChange({ ...value, ...toOrder(c) }); setOpen(false); }}
                className="block w-full px-3 py-2 text-left transition-colors hover:bg-blue-50">
                <p className="text-sm font-semibold">{c.name}</p>
                <p className="text-xs text-slate-500">{[c.phone, c.address].filter(Boolean).join(" · ")}</p>
              </button>
            ))}
          </div>
        )}
      </div>
      {value.customer_id && (
        <button type="button" data-testid="order-customer-clear" onClick={() => onChange({ customer_id: "", customer_name: "", customer_phone: "", customer_address: "", customer_tax_office: "", customer_tax_no: "", note: value.note })}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600"><X className="h-3 w-3" /> Müşteri seçimini kaldır</button>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Input data-testid="order-customer-phone-input" placeholder="Telefon" value={value.customer_phone} onChange={(e) => onChange({ ...value, customer_phone: e.target.value })} />
        <Input data-testid="order-customer-taxno-input" placeholder="Vergi No" value={value.customer_tax_no} onChange={(e) => onChange({ ...value, customer_tax_no: e.target.value })} />
      </div>
      <Input data-testid="order-customer-taxoffice-input" placeholder="Vergi Dairesi" value={value.customer_tax_office} onChange={(e) => onChange({ ...value, customer_tax_office: e.target.value })} />
      <Input data-testid="order-customer-address-input" placeholder="Adres" value={value.customer_address} onChange={(e) => onChange({ ...value, customer_address: e.target.value })} />
      {!value.customer_id && value.customer_name.trim() && (
        <button type="button" data-testid="order-customer-save-button" onClick={save}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:underline"><UserPlus className="h-4 w-4" /> Bu müşteriyi listeye kaydet</button>
      )}
    </div>
  );
};
